import { cn } from '@/lib/utils';
import { Sparkles, Settings, Home, Building2, KeyRound, Check } from 'lucide-react';
import type { PropertiesResponse } from '@/lib/types';

export type StatusFilter = '' | '出售' | '出租' | '已售';

interface Props {
	currentStatus: StatusFilter;
	onChange: (s: StatusFilter) => void;
	stats: { total: number; sale: number; rent: number; sold: number };
	onOpenChat: () => void;
	open: boolean;
	onClose: () => void;
}

export function Sidebar({ currentStatus, onChange, stats, onOpenChat, open, onClose }: Props) {
	const items: { key: StatusFilter; label: string; ct: number; icon: React.ElementType; dotClass: string; activeClass: string }[] = [
		{ key: '', label: '所有房源', ct: stats.total, icon: Home, dotClass: 'bg-accent', activeClass: 'bg-green-100 text-accent border-green-300' },
		{ key: '出售', label: '出售中', ct: stats.sale, icon: Building2, dotClass: 'bg-sale', activeClass: 'bg-orange-100 text-orange-900 border-orange-300' },
		{ key: '出租', label: '出租中', ct: stats.rent, icon: KeyRound, dotClass: 'bg-rent', activeClass: 'bg-blue-100 text-blue-900 border-blue-300' },
		{ key: '已售', label: '已成交', ct: stats.sold, icon: Check, dotClass: 'bg-sold', activeClass: 'bg-zinc-200 text-zinc-800 border-zinc-300' },
	];
	return (
		<>
			{open && (
				<div className="fixed inset-0 bg-black/30 z-40 md:hidden" onClick={onClose} />
			)}
			<aside
				className={cn(
					'w-[232px] flex-shrink-0 bg-[#fafafa] border-r border-line p-3 sticky top-0 h-screen overflow-y-auto z-50',
					'md:translate-x-0 transition-transform fixed md:sticky',
					open ? 'translate-x-0 shadow-soft-lg' : '-translate-x-full md:translate-x-0'
				)}
			>
				<div className="flex items-center gap-2.5 px-2 pb-4 pt-1">
					<div className="w-6 h-6 bg-accent rounded-md text-white text-[14px] font-bold flex items-center justify-center">房</div>
					<div className="text-[16.5px] font-semibold tracking-tight">房源工作台</div>
				</div>

				<div className="mb-3">
					<div className="text-[13px] text-ink-3 px-2 py-1.5 font-medium uppercase tracking-wider">视图</div>
					{items.map((it) => {
						const active = currentStatus === it.key;
						const Icon = it.icon;
						return (
							<button
								key={it.key}
								onClick={() => {
									onChange(it.key);
									onClose();
								}}
								className={cn(
									'w-full flex items-center gap-2 px-2 py-1.5 rounded-md border border-transparent text-[15.5px] transition-colors',
									active ? cn('font-medium', it.activeClass) : 'text-ink-2 hover:bg-[#f4f4f3] hover:text-ink'
								)}
							>
								<Icon size={14} className={cn('flex-shrink-0', active ? 'text-current' : 'text-ink-3')} />
								<span className={cn('w-1.5 h-1.5 rounded-full flex-shrink-0', it.dotClass)} />
								<span className="flex-1 text-left">{it.label}</span>
								<span className={cn('rounded-full px-1.5 py-0.5 text-[13.5px] tabular', active ? 'bg-white/70 text-current' : 'text-ink-3')}>{it.ct}</span>
							</button>
						);
					})}
				</div>

				<div className="mb-3">
					<div className="text-[13px] text-ink-3 px-2 py-1.5 font-medium uppercase tracking-wider">工具</div>
					<button
						onClick={() => {
							onOpenChat();
							onClose();
						}}
						className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-[15.5px] text-ink-2 hover:bg-[#f4f4f3] hover:text-ink"
					>
						<Sparkles size={13} className="text-ink-3" /> AI 助手
					</button>
				</div>

				<div className="mt-auto absolute bottom-3 left-3 right-3 text-[13px] text-ink-4">
					<div className="flex items-center gap-1.5">
						<Settings size={11} /> 房源系统 · v2
					</div>
				</div>
			</aside>
		</>
	);
}

export type { PropertiesResponse };
